import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IComparison, IComparisonOfferLine, IComparisonSection } from '@app/api/comparison.api';
import { getInsuranceCompanyImage } from '@app/constants/insuranceCompanies';
import { getAWSSignedURL } from '@app/api/file.api';
import { ComparisonSide } from '@app/constants/enums/comparison';
import { useInView } from '@app/hooks/useInView';
import barozLogo from 'assets/images/landing/baroz-logo.png';
import * as S from './Comparison.styles';
import { renderRichText } from './richText';

interface ComparisonProps {
  comparison?: IComparison | null;
}

const asText = (value: unknown): string | null => (typeof value === 'string' && value.trim() ? value : null);

const isUrl = (value: string): boolean => /^https?:\/\//i.test(value);

/**
 * A PDF arrives as a File id, so its signed URL is fetched only once the link is
 * clicked — one signed here on page load would expire under a reader who opens
 * it later. The tab is opened first, while still inside the click, or the popup
 * blocker would swallow it after the await.
 */
const openComparisonFile = async (asset: string): Promise<void> => {
  // No `noopener` here: it makes window.open return null, and the tab would then
  // be opened a second time by the fallback below. The opener is severed instead.
  const tab = window.open('', '_blank');
  if (tab) tab.opener = null;

  try {
    const url = isUrl(asset) ? asset : await getAWSSignedURL(asset);
    if (!url) {
      tab?.close();
      return;
    }
    if (tab) tab.location.href = url;
    // Only reached when the blank tab was blocked, so nothing is open to reuse.
    else window.open(url, '_blank', 'noopener,noreferrer');
  } catch {
    tab?.close();
  }
};

const getHeader = (section: IComparisonSection, side: ComparisonSide): string | null => asText(section[`${side}Text`]);

const toOrder = (value?: number | null): number => (typeof value === 'number' ? value : 0);

const getOfferLines = (section: IComparisonSection, side: ComparisonSide): IComparisonOfferLine[] =>
  [...(section[`${side}OfferLines`] || [])]
    .filter((offerLine) => offerLine && (asText(offerLine.text) || asText(offerLine.Image)))
    .sort((a, b) => toOrder(a.line) - toOrder(b.line));

/** Every text field is optional, so a section with nothing on either side is dropped rather than rendered empty. */
const hasContent = (section: IComparisonSection): boolean =>
  Object.values(ComparisonSide).some(
    (side) => asText(section[`${side}Text`]) !== null || getOfferLines(section, side).length > 0,
  );

/**
 * A comparison image is stored as a File id and signed only when this mounts —
 * and a panel mounts its content just as it opens. The signed URL expires within
 * seconds, so signing it at deal-room-response time would leave it stale before
 * the reader ever expands the section; fetching here ties the fresh URL to the
 * open. A caller-supplied `https://` value has no expiry and renders directly.
 */
const useSignedSrc = (asset: string | null): string | null => {
  const [src, setSrc] = useState<string | null>(null);

  useEffect(() => {
    if (!asset) return undefined;
    // A caller-supplied https URL has no expiry and needs no round trip.
    if (isUrl(asset)) {
      setSrc(asset);
      return undefined;
    }
    let active = true;
    getAWSSignedURL(asset).then((url) => {
      if (active && url) setSrc(url);
    });
    return () => {
      active = false;
    };
  }, [asset]);

  return src;
};

const ComparisonImage: React.FC<{ asset: string; alt: string }> = ({ asset, alt }) => {
  const src = useSignedSrc(asset);

  if (!src) return null;
  return <S.ImageBlock src={src} alt={alt} loading="eager" decoding="async" />;
};

/**
 * Decorative beside the title, so it carries no alt text of its own. The element
 * is in the DOM from the start — it is what the observer watches, and it holds
 * the icon's space so the header does not jump when the artwork arrives.
 *
 * `loading="lazy"` would be the wrong tool here: the browser defers the fetch on
 * its own schedule, by which time a ~30s signed URL may already be spent. The
 * signing is what waits instead, and the fetch follows it immediately.
 */
const SectionIcon: React.FC<{ asset: string }> = ({ asset }) => {
  const ref = useRef<HTMLImageElement>(null);
  const src = useSignedSrc(useInView(ref) ? asset : null);

  return <S.SectionIcon ref={ref} src={src ?? undefined} alt="" decoding="async" />;
};

const svgProps = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
} as const;

const CheckIcon = (): JSX.Element => (
  <svg width="14" height="14" strokeWidth="3" {...svgProps}>
    <path d="M20 6L9 17l-5-5" />
  </svg>
);

const WarningIcon = (): JSX.Element => (
  <svg width="14" height="14" strokeWidth="2.4" {...svgProps}>
    <path d="M12 3L2 20h20L12 3z" />
    <path d="M12 10v4" />
    <circle cx="12" cy="17" r="0.6" fill="currentColor" stroke="none" />
  </svg>
);

const CheckCircleIcon = (): JSX.Element => (
  <svg width="20" height="20" strokeWidth="2" {...svgProps}>
    <circle cx="12" cy="12" r="9" />
    <path d="M8 12.5l2.5 2.5L16 9" />
  </svg>
);

const CrossCircleIcon = (): JSX.Element => (
  <svg width="20" height="20" strokeWidth="2" {...svgProps}>
    <circle cx="12" cy="12" r="9" />
    <path d="M9.5 9.5l5 5M14.5 9.5l-5 5" />
  </svg>
);

/** A logo when the insurer has one; otherwise its initial in a chip, with the name beside it. */
const renderCompany = (preferred: boolean, name?: string | null, image?: string | null): React.ReactNode => {
  const logo = getInsuranceCompanyImage(image);
  if (logo) {
    return (
      <S.CompanyLogoBox>
        <S.CompanyLogo src={logo} alt={name || ''} loading="lazy" decoding="async" />
      </S.CompanyLogoBox>
    );
  }
  if (!asText(name)) return <span />;
  return (
    <S.Company>
      <S.CompanyChip $preferred={preferred}>{name?.trim().charAt(0)}</S.CompanyChip>
      <S.CompanyName>{name}</S.CompanyName>
    </S.Company>
  );
};

/** `הצעת בר-עוז (מנורה): …` — caption, the insurer in parentheses, then the text. */
const renderIntroLine = (key: string, label: string, text: string, company?: string | null): JSX.Element => (
  <S.IntroLine key={key}>
    <S.IntroLabel>{`${label}${asText(company) ? ` (${company})` : ''}: `}</S.IntroLabel>
    {renderRichText(text)}
  </S.IntroLine>
);

const renderOfferLine = (
  offerLine: IComparisonOfferLine,
  alt: string,
  accent: string,
  preferred: boolean,
  withMarker: boolean,
  key: string,
): JSX.Element => {
  const text = asText(offerLine.text);
  const image = asText(offerLine.Image);
  return (
    <S.OfferLine key={key} $preferred={preferred}>
      {withMarker && (preferred ? <CheckCircleIcon /> : <CrossCircleIcon />)}
      <S.OfferLineContent>
        {text && <S.OfferLineText>{renderRichText(text, accent)}</S.OfferLineText>}
        {image && <ComparisonImage asset={image} alt={alt} />}
      </S.OfferLineContent>
    </S.OfferLine>
  );
};

export const Comparison: React.FC<ComparisonProps> = ({ comparison }) => {
  const { t } = useTranslation();

  if (!comparison) return <></>;

  const sections = [...(comparison.sections || [])]
    .filter((section) => section && hasContent(section))
    .sort((a, b) => toOrder(a.order) - toOrder(b.order));
  const fileLabel = t('dealRooms.landing.comparisonFile', 'קובץ השוואה');

  const barozOffer = asText(comparison.barozText);
  const competitorOffer = asText(comparison.competitorText);
  const summary = asText(comparison.summary);
  const comparisonPdf = asText(comparison.comparisonPdf);

  const introLines = [
    barozOffer &&
      renderIntroLine(
        'baroz',
        t('dealRooms.landing.comparisonBarozOffer', 'הצעת בר-עוז'),
        barozOffer,
        comparison.barozOfferCompany,
      ),
    competitorOffer &&
      renderIntroLine(
        'competitor',
        t('dealRooms.landing.comparisonCompetitorOffer', 'הצעה להשוואה'),
        competitorOffer,
        comparison.competitorOfferCompany,
      ),
    summary && renderIntroLine('summary', t('dealRooms.landing.comparisonSummary', 'תקציר'), summary),
    // The whole-comparison PDF: linked, not rendered — its signed URL is fetched on click.
    comparisonPdf && (
      <S.FileLink key="pdf" type="button" onClick={() => openComparisonFile(comparisonPdf)}>
        {fileLabel}
      </S.FileLink>
    ),
  ].filter(Boolean) as JSX.Element[];

  const renderSide = (section: IComparisonSection, side: ComparisonSide, index: number): JSX.Element | null => {
    const header = getHeader(section, side);
    const offerLines = getOfferLines(section, side);
    if (!header && offerLines.length === 0) return null;

    const sectionTitle = asText(section.section) || '';
    const isBaroz = side === ComparisonSide.BAROZ;
    const company = isBaroz ? comparison.barozOfferCompany : comparison.competitorOfferCompany;
    const companyImage = isBaroz ? comparison.barozOfferCompanyImage : comparison.competitorOfferCompanyImage;
    const accent = isBaroz ? S.NAVY : S.LESS_GOOD_COLOR;

    return (
      <S.Card key={`${side}-${index}`} $preferred={isBaroz}>
        {isBaroz && (
          <S.BrandNote>
            <S.BarozLogo src={barozLogo} alt="בר-עוז" />
            <S.BrandText>
              {t(
                'dealRooms.landing.comparisonBarozTagline',
                'סוכנות ביטוח הגדולה, המובילה, והיחידה שמתמקדת אך ורק בתחום בתים משותפים.',
              )}
            </S.BrandText>
          </S.BrandNote>
        )}
        <S.CardHeader $preferred={isBaroz}>
          {renderCompany(isBaroz, company, companyImage)}
          <S.Verdict $preferred={isBaroz}>
            {isBaroz ? <CheckIcon /> : <WarningIcon />}
            {isBaroz
              ? t('dealRooms.landing.comparisonPreferred', 'הצעה מועדפת')
              : t('dealRooms.landing.comparisonLessGood', 'הצעה פחות טובה')}
          </S.Verdict>
        </S.CardHeader>

        {header && (
          <S.HeaderDescription $preferred={isBaroz}>
            <S.HeaderDescriptionText>{renderRichText(header, accent)}</S.HeaderDescriptionText>
          </S.HeaderDescription>
        )}
        {offerLines.map((offerLine, lineIndex) =>
          renderOfferLine(offerLine, sectionTitle, accent, isBaroz, lineIndex === 0, `${side}-${index}-${lineIndex}`),
        )}
      </S.Card>
    );
  };

  if (introLines.length === 0 && sections.length === 0) return <></>;

  return (
    <S.Container $flushTop={introLines.length > 0}>
      {introLines.length > 0 && (
        <S.Intro>
          {introLines.flatMap((line, index) =>
            index === 0 ? [line] : [<S.IntroDivider key={`intro-divider-${line.key}`} />, line],
          )}
        </S.Intro>
      )}
      {/* A Container child, so the rule keeps the same 16px above it as below it,
          whichever of the headline parameters rendered. */}
      {introLines.length > 0 && sections.length > 0 && <S.SectionDivider />}

      {sections.flatMap((section, index) => {
        const icon = asText(section.icon);
        const sectionNode = (
          <S.Section key={`${toOrder(section.order)}-${index}`}>
            <S.SectionHeader>
              <S.SectionNumber>{index + 1}</S.SectionNumber>
              {asText(section.section) && <S.SectionTitle>{section.section}</S.SectionTitle>}
              {icon && <SectionIcon asset={icon} />}
            </S.SectionHeader>

            {renderSide(section, ComparisonSide.BAROZ, index)}
            {renderSide(section, ComparisonSide.COMPETITOR, index)}
          </S.Section>
        );

        return index === 0 ? [sectionNode] : [<S.SectionDivider key={`divider-${index}`} />, sectionNode];
      })}
    </S.Container>
  );
};
